/* eslint-disable camelcase */
import React, {
	Suspense,
	useEffect,
	useState,
	useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { isEqual } from 'underscore';
import { GreenroomState as GS } from '../../stores/types/types';
import Socket from '../../connection/socket';
import Header from './header';
import MainVideoContainer from '../main-video/main-video-container';
import RightPanelContainer from '../right-panel/right-panel-container';
import ShowboardDrawer from '../showboard/showboard-drawer';
import WaitingIndicator from '../general-components/waiting-indicator';
import * as ShotsActions from '../../stores/actions/shots';
import * as SessionActions from '../../stores/actions/session';
import * as ChatActions from '../../stores/actions/chat';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import * as VideoActions from '../../stores/actions/video';
import { RequestSyncedState } from '../../connection/sync';
import {
	EMessageTypes,
	EventsMessage,
	EventsMessageChat,
	EventsMessageComment,
	EventsMessageQuestion,
} from '../../stores/reducers/chat';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import { Shot } from '../../stores/reducers/shots';
import * as Storage from '../../utils/local-storage';
import DebugPanel, { DebugLocalStorage } from '../debug-views/debug-panel/panel';
import useMediaNotifications from '../../hooks/use-media-notifications';
import { appLogger } from '../../utils/datadog';
import useLoad from '../../hooks/use-load';
import useAutoplayNotify from '../../hooks/use-autoplay-notify';
import useResolutionUpdate from '../../hooks/use-resolution-update';
import Portal from '../general-components/portal';
import useUnmuteSnackbar from '../../hooks/use-unmute-snackbar';
import EmbedController from './embed-controller';
import FullscreenTransition from './fullscreen-transition';
import { setRoomState, setRoomStateTransitioning } from '../../stores/actions/meeting';
import { setShowDeprecationWarning } from '../../stores/actions/user-interface';
import usePrevious from '../../utils/use-previous';
import { RegenerateStaleContentForMeeting } from '../../connection/generate-content';
import { setModalState } from '../../stores/actions/modal';

interface CreateEventsMessage {
	type: 'create_events_message';
	data: EventsMessage;
}

interface DeleteEventsMessage {
	type: 'delete_events_message';
	data: {
		id: number;
		messageType: EMessageTypes;
	}
}

interface Props {
	socket: Socket | null;
	startOptions: any;
}

export default function MeetingRoom(props: Props) {
	const { socket } = props;
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const session = useSelector((s: GS) => s.SessionReducer.session);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const watcher_only = useSelector((s: GS) => s.SessionReducer.watcher_only);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const shotsLoaded = useSelector((s: GS) => s.ShotsReducer.shotsLoaded);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const mediaLoaded = useSelector((s: GS) => s.MediaReducer.mediaLoaded);
	const hasSynced = useSelector((s: GS) => s.SettingsReducer.hasSynced);
	const useModeratedQuestions = useSelector((s: GS) => s.SettingsReducer.useModeratedQuestions);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const modalTemplate = useSelector((s: GS) => s.ModalReducer.modalTemplate);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const broadcastStartedBy = useSelector((s: GS) => s.BroadcastReducer.broadcastStartedBy);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const mediaPagesLoaded = useSelector((s: GS) => s.MediaReducer.mediaPagesLoaded);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const roomStateEntity = useSelector((s: GS) => s.MeetingReducer.roomStateEntity);
	const roomStateTransitioning = useSelector((s: GS) => s.MeetingReducer.roomStateTransitioning);
	const { debugEnabled } = useSelector((s: GS) => s.DebugReducer);
	const currRecordingStreamId = useSelector((s: GS) => s.BroadcastReducer.currentRecordingStreamId);
	const brdcstStartingShotId = useSelector((s: GS) => s.BroadcastReducer.broadcastStartingShotId);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const { 
		use_spielberg: useSpielberg,
		use_title_library: useTitleLibrary
	} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	useResolutionUpdate();

	const dispatch = useDispatch();
	useMediaNotifications();
	// this should be the generic room load - you need to load the media, shots,
	// content, etc before showing UI
	const [loadingRoomContent, setLoadingRoomContent] = useState<boolean>(true);
	const [hasInitiatedContentLoad, setHasInitiatedContentLoad] = useState<boolean>(false);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [watcherOnly, setWatcherOnly] = useState<boolean | null>(null);

	const eventsMessagesSocket = useRef<Socket | null>(null);

	const { loadMeetingRoom, loadModeratedChats } = useLoad();

	// Notify users if autoplay was toggled off due to the
	// edited shot no longer being auto duration.
	useAutoplayNotify();

	const unmuteSnackbar = useUnmuteSnackbar({
		showForSeconds: 10,
	});

	// on component mount
	useEffect(() => {
		if (meeting && !meeting.events_session_uuid) {
			dispatch(setShowDeprecationWarning(true));
		}

		if (!embed && socket) {
			socket.sendMessage('user-entered-room', { joining: user, watcher_only, from: session });
		}

		// embed view will be responsible for editing recording metadata
		if (embed && socket) {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			socket.addListener('ivs-recording-created', handleRecordingCreated);
		}

		if (meeting && host) {
			RegenerateStaleContentForMeeting(meeting.meeting).then((staleContent) => {
				if (useTitleLibrary && staleContent.length > 0) {
					dispatch(setModalState({
						modalOpen: true,
						modalTemplate: MODAL_TEMPLATES.GENERATED_CONTENT_UPDATING,
						modalData: { staleContent },
					}));
				}
			});
		}

		return () => {
			socket?.removeListener('ivs-recording-created');
		};
	}, []);

	// if invitation changes from viewer to presenter or vice versa
	// reload user to connect to opentok properly
	useEffect(() => {
		const invitation = invitations.find((_invitation) => (_invitation.profile.gr_user
			&& _invitation.profile.gr_user === user?.gr_user)
			|| (_invitation.profile.email && _invitation.profile.email === user?.email));

		if (invitation) {
			dispatch(SessionActions.setWatcherOnly(host ? false : (invitation.watcher_only ?? false)));
		}
	}, [invitations, host]);

	// update recording metadata when there is a change
	useEffect(() => {
		if (
			embed
			&& token
			&& currRecordingStreamId
			&& broadcastStartedBy !== 0
			&& brdcstStartingShotId !== 0
		) {
			dispatch(
				BroadcastActions.editRecordingMetadata(
					token,
					currRecordingStreamId,
					broadcastStartedBy,
					brdcstStartingShotId,
				),
			);
		}
	}, [broadcastStartedBy, brdcstStartingShotId, meeting, currRecordingStreamId]);

	// load meeting data here please
	useEffect(() => {
		if (!hasInitiatedContentLoad) {
			if (token && meeting) {
				/* Call with `isReload` === false and we will set
				`hasInitiatedContentLoad` = true once everything has successfully loaded. */

				loadMeetingRoom({
					passedInToken: token,
					passedInMeeting: meeting,
					embed,
					isReload: false,
					successCallback: () => {
						setHasInitiatedContentLoad(true);
					},
				});
			}

			// Handle loading initial Events Q&A messages coming from E3 moderator
			loadModeratedChats();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, meeting, embed, hasInitiatedContentLoad]);

	useEffect(() => {
		// not sure if we should also check for invites/rehearsals/tech checks,
		// but media and shots are necessary
		if (
			// we probably don't need to gate entry for this tbh, if media load
			// fails id rather they get into the room...
			mediaLoaded
			// Additional media info for pages (i.e. volume and mute state)
			&& mediaPagesLoaded
			// shots are necessary, if shot load fails I don't know how greenroom would work
			&& shotsLoaded
			// hm...
			&& shots[0]
			// hasSynced gates room entry until beehive sync is complete
			&& hasSynced
		) { setLoadingRoomContent(false); }
	}, [mediaLoaded, shotsLoaded, shots, hasSynced]);

	// inform the LiveKit Egress service to start recording/livestreaming once the meeting is loaded
	useEffect(() => {
		if (!loadingRoomContent && embed) {
			console.log('START_RECORDING');
		}
	}, [loadingRoomContent]);

	// get or provision a Bullman server if we turn the Spielberg feature flag from on to off
	const prevUseSpielberg = usePrevious(useSpielberg);
	useEffect(() => {
		if (prevUseSpielberg && !useSpielberg && token && meeting) {
			dispatch(BroadcastActions.getBroadcastServer(token, meeting.meeting));
		}
	}, [
		useSpielberg,
		prevUseSpielberg,
		token,
		meeting,
	]);

	const handleRecordingCreated = (message: any) => {
		if (!meeting || !message.message?.ivsRecordingStreamId || !token) {
			return;
		}

		// get recording metadata
		RequestSyncedState(meeting.meeting, token);
		dispatch(BroadcastActions.setCurrentRecordingStreamId(message.message.ivsRecordingStreamId));
	};

	function handleCreateEventsMessage(msg: CreateEventsMessage): void {
		const { data, data: { id, message_type } } = msg;

		let message;

		switch (message_type) {
			case EMessageTypes.Chat:
				message = data as EventsMessageChat;
				break;
			case EMessageTypes.Question:
				message = data as EventsMessageQuestion;
				break;
			case EMessageTypes.QuestionComment:
				message = data as EventsMessageComment;
				break;
			default:
				break;
		}

		if (message) {
			dispatch(
				ChatActions.createModeratedEventsMessages(
					`${message_type}-${id}`,
					message,
				),
			);
		}
	}

	function handleDeleteEventsMessage(msg: DeleteEventsMessage) {
		const { data: { id, messageType } } = msg;

		dispatch(
			ChatActions.deleteModeratedEventsMessages(`${messageType}-${id}`),
		);
	}

	useEffect(() => {
		// DEPRECATED - need until events-api release after 1009 work (new hook below)
		if (useModeratedQuestions) {
			if (meeting?.events_session_uuid) {
				eventsMessagesSocket.current = new Socket(`events_greenroom_messages-${meeting?.events_session_uuid}`);
				eventsMessagesSocket.current.addListener('create_events_message', handleCreateEventsMessage);
				eventsMessagesSocket.current.addListener('delete_events_message', handleDeleteEventsMessage);

				// Download E3 presenter messages if socket reconnected
				eventsMessagesSocket.current.onReconnected = () => {
					loadModeratedChats();
				};
			}
		}
		return () => {
			eventsMessagesSocket.current?.removeListener('create_events_message');
			eventsMessagesSocket.current?.removeListener('delete_events_message');
			eventsMessagesSocket.current?.disconnect();
			eventsMessagesSocket.current = null;
		};
	}, [meeting?.events_session_uuid, useModeratedQuestions]);

	// Handle live Events Q&A messages coming from E3 moderator
	useEffect(() => {
		// NEW - functionality replacing above hook
		if (useModeratedQuestions && meeting?.events_session_uuid && socket) {
			socket.addListener('create_events_message', handleCreateEventsMessage);
			socket.addListener('delete_events_message', handleDeleteEventsMessage);
		}

		return () => {
			socket?.removeListener('create_events_message');
			socket?.removeListener('delete_events_message');
		};
	}, [meeting?.events_session_uuid, useModeratedQuestions]);

	useEffect(() => {
		/*
		This hook originally resided in the use-shots.ts custom hook where it
		was likely partially responsible for causing the Aw Snap crashes from
		GREEN-2352.

		It was firing upwards of 60 times on switching shots and causing
		noticeable performance issues - especially high memory use.

		It is being moved here with an additional equality check to make sure
		we are updating `shotModalShot` when needed without
		causing the issues mentioned above.

		There still may be additional performance optimization opportunities.

		OG comment:

		if we're NOT editing a shot and the shot corresponding to our
		`shotModalShot` changes (someone else edited it), update
		our `shotModalShot`
		*/

		if (
			!modalOpen
			|| (modalOpen && modalTemplate !== MODAL_TEMPLATES.SHOT_BUILDER)
		) {
			const updatedShot = shots.find(
				(shot: Shot) => shot.shot_id === shotModalShot.shot_id,
			);

			if (updatedShot) {
				/* This is intended to limit unnecessary updates (and infinite
				loops) by checking if `updatedShot` !==
				`shotModalShot` and only allowing the update
				if that is the case. Because these are objects, we have to
				check object property equality with `isEqual`. */

				const shouldUpdate = !isEqual(
					updatedShot,
					shotModalShot,
				);

				if (shouldUpdate) {
					dispatch(ShotsActions.setShotModalShot(updatedShot));
				}
			}
		}
	}, [modalOpen, modalTemplate, shotModalShot, shots]);

	/* This is setting the group message local storage to the current user.
	It will be cleared if another user logs in so we don't show incorrect
	counts for different users. */
	useEffect(() => {
		if (meeting?.meeting && user?.gr_user) {
			const currentChatRoomsStatus:
				| { [key: number | string]: number }
				| undefined = Storage.getStorageObject(
					`chat-rooms-last-seen-${meeting.meeting}-${user.gr_user}`,
				);

			if (!currentChatRoomsStatus) {
				Storage.setStorageObject(`chat-rooms-last-seen-${meeting?.meeting}-${user.gr_user}`, {
					gr_user: user.gr_user,
				});
			}
		}
	}, [meeting, user]);

	useEffect(() => () => {
		// reset redux states when leaving a meeting
		dispatch(VideoActions.setConnectedPresenterVideos([]));
		dispatch(BroadcastActions.resetBroadcastReducerState());
		socket?.disconnect();
	}, []);

	useEffect(() => {
		let doToggle = localStorage.getItem(DebugLocalStorage.PanelEnabled);
		const staleFlag = Storage.getStorageItem(DebugLocalStorage.StalePanelEnabled);
		if (staleFlag === 'true' || staleFlag === 'false') {
			Storage.removeStorageItem(DebugLocalStorage.StalePanelEnabled);
			if (staleFlag === 'true') {
				doToggle = 'true';
			}
		}
		if (doToggle !== 'true' && user?.email?.includes('@brandlive.com')) {
			doToggle = new URLSearchParams(window.location.search).get('panel-open');
		}
		if (doToggle === 'true') {
			dispatch({ type: 'DEBUG_TOGGLE_DEBUG', payload: true });
		}
	}, [user?.email]);

	useEffect(() => {
		// if we are at this point in the app's component hierarchy,
		// session should have been defined in app.tsx
		// SET_PAGE_STATUS may be overwriting session and causing bugs
		if (!session) {
			appLogger.warn('no session defined');
		}
	}, [session]);

	useEffect(() => {
		if (roomStateEntity && roomStateEntity.entityValue.roomState !== roomState) {
			if (host && !loadingRoomContent) {
				dispatch(setRoomStateTransitioning(true));
			} else {
				dispatch(setRoomState(roomStateEntity.entityValue.roomState));
			}
		}
	}, [host, roomStateEntity, roomState, loadingRoomContent]);

	const WaitingJSX = <div className="wrapper loading-right-panel"><WaitingIndicator /></div>;

	return ((loadingRoomContent && !embed)
		? <div className={classNames('wrapper animate', { embed })}>
			<WaitingIndicator />
		</div>
		: <div className={classNames('wrapper animate', { embed })}>
			{embed && <EmbedController />}
			{roomStateTransitioning && <FullscreenTransition />}
			<div className="meeting-wrapper">
				{!!debugEnabled && <DebugPanel socket={socket} />}
				<div className="meeting-header">
					{!embed && <Header />}
				</div>
				<div className="meeting-body">
					<div className={'left-panel'}>
						<MainVideoContainer
							socket={socket}
						/>
						{!embed && <>
							<Portal targetId={`portal-unmute-snackbar-${roomState}`}>
								{unmuteSnackbar}
							</Portal>
						</>}
						{!embed && <Suspense fallback={WaitingJSX}>
							<ShowboardDrawer socket={socket}/>
						</Suspense>}
					</div>
					<div style={embed ? { display: 'none' } : {}} className={classNames('right-panel')}>
						<Suspense fallback={WaitingJSX}>
							<RightPanelContainer
								socket={socket}
							/>
						</Suspense>
					</div>
				</div>
			</div>
			<div id="right-click-portal" />
		</div>
	);
}
